import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/shared/toaster'
import { accessToken } from '@/shared/auth'
import { getUserControllerGetMeQueryKey } from '@/api/users/users'
import { getWalletControllerGetWalletsQueryKey } from '@/api/wallet/wallet'
import { compareWalletAddresses } from './wallet-utils'
import type { Wallet } from '@tonconnect/ui-react'

const BASE_URL =
  import.meta.env.VITE_USE_PROXY === 'true'
    ? '/api'
    : import.meta.env.VITE_API_URL || ''

interface WalletProof {
  timestamp: number
  domain: {
    lengthBytes: number
    value: string
  }
  payload: string
  signature: string
}

interface VerifyProofParams {
  wallet: Wallet
  onSuccess?: () => void
  onError?: () => void
}

/**
 * Хук для верификации TON Proof на backend
 * Использует useMutation для предотвращения дублирующихся запросов
 */
export const useWalletVerification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    // mutationKey будет динамическим при вызове mutate()
    mutationFn: async ({ wallet }: VerifyProofParams) => {
      const tonProof = wallet.connectItems?.tonProof
      if (!tonProof || !('proof' in tonProof) || !tonProof.proof) {
        throw new Error('Proof not available')
      }

      const proof = tonProof.proof as WalletProof
      const network = import.meta.env.VITE_CONNECT_NETWORK

      const body = {
        address: wallet.account.address,
        network,
        public_key: wallet.account.publicKey,
        proof: {
          timestamp: proof.timestamp,
          domain: {
            lengthBytes: proof.domain.lengthBytes,
            value: proof.domain.value,
          },
          payload: proof.payload,
          signature: proof.signature,
          state_init: wallet.account.walletStateInit,
        },
        payloadToken: proof.payload,
      }

      const res = await fetch(`${BASE_URL}/wallet/check-proof`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken.value && {
            Authorization: `Bearer ${accessToken.value}`,
          }),
        },
        body: JSON.stringify(body),
        credentials: 'include',
      })

      if (!res.ok) {
        const errorText = await res.text()

        // Проверяем тип ошибки
        const isUniqueConstraint =
          res.status === 500 && errorText.includes('Unique constraint failed')
        const isPayloadError =
          errorText.toLowerCase().includes('payload') ||
          errorText.toLowerCase().includes('expired')

        // Unique constraint - кошелек точно уже есть на бэке
        if (isUniqueConstraint) {
          // refetchQueries возвращает Promise после загрузки данных
          await queryClient.refetchQueries({
            queryKey: getUserControllerGetMeQueryKey(),
          })
          await queryClient.refetchQueries({
            queryKey: getWalletControllerGetWalletsQueryKey(),
          })
          return
        }

        // Payload error - кошелек возможно уже сохранен (дубликат запроса)
        // Обновляем данные и проверяем
        if (isPayloadError) {
          await queryClient.refetchQueries({
            queryKey: getWalletControllerGetWalletsQueryKey(),
          })

          const currentWalletAddress = wallet.account.address
          const wallets = queryClient.getQueryData<
            Array<{ isActive: boolean; walletAddress: string }>
          >(getWalletControllerGetWalletsQueryKey())

          const backendWalletAddress = wallets?.find(
            (w) => w.isActive,
          )?.walletAddress

          if (
            backendWalletAddress &&
            compareWalletAddresses(currentWalletAddress, backendWalletAddress)
          ) {
            // Кошелек есть на бэке - всё ОК
            return
          }

          // Кошелька нет на бэке - критичная ошибка
          throw new Error('Кошелек не был сохранен на сервере')
        }

        throw new Error(`Ошибка верификации: ${errorText}`)
      }

      // Обновляем данные на фронте после успешной верификации
      // refetchQueries возвращает Promise после загрузки - данные сразу в кэше
      await queryClient.refetchQueries({
        queryKey: getUserControllerGetMeQueryKey(),
      })
      await queryClient.refetchQueries({
        queryKey: getWalletControllerGetWalletsQueryKey(),
      })
    },
    onError: (error, variables) => {
      const message =
        error instanceof Error ? error.message : 'Ошибка верификации кошелька'
      toast.error(message)
      variables.onError?.()
    },
    onSuccess: (_, variables) => {
      variables.onSuccess?.()
    },
  })
}
